import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Mail, Phone, Globe } from 'lucide-react';
import { useLegal } from '../../components/common/LegalCompany';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    body: (
      <>
        <p>To provide our Cable TV, Broadband, IPTV, and related services, we may collect the following information.</p>
        <h4>a) Personal Information</h4>
        <p>When you register, make payments, submit requests, or contact us, we may collect:</p>
        <ul>
          <li>Full Name</li>
          <li>Mobile Number</li>
          <li>Email Address</li>
          <li>Residential/Billing Address</li>
          <li>Customer ID or Account Number</li>
          <li>Service Installation Address</li>
          <li>Payment Information (processed securely through authorized third-party payment gateways)</li>
        </ul>
        <h4>b) Automatically Collected Information</h4>
        <p>When you use our website, we may automatically collect:</p>
        <ul>
          <li>IP Address</li>
          <li>Browser Type</li>
          <li>Device Information</li>
          <li>Operating System</li>
          <li>Website Usage Statistics</li>
          <li>Date and Time of Access</li>
          <li>Cookies and Similar Technologies</li>
        </ul>
      </>
    ),
  },
  {
    title: '2. How We Use Your Information',
    body: (
      <>
        <p>We use your information to:</p>
        <ul>
          <li>Provide and manage Cable TV, Broadband, IPTV, and related services</li>
          <li>Process payments and recharge requests</li>
          <li>Verify customer identity</li>
          <li>Respond to customer support requests</li>
          <li>Send service updates, outage notifications, billing reminders, and other account-related communications</li>
          <li>Improve our website, services, and overall customer experience</li>
          <li>Detect, investigate, and prevent fraud or unauthorized access</li>
          <li>Comply with applicable laws and regulatory requirements</li>
        </ul>
        <p>We collect only the information necessary to provide and improve our services.</p>
      </>
    ),
  },
  {
    title: '3. Cookies',
    body: (
      <>
        <p>Our website uses cookies and similar technologies to:</p>
        <ul>
          <li>Keep you signed in</li>
          <li>Remember your preferences</li>
          <li>Improve website performance</li>
          <li>Analyze website traffic and usage</li>
        </ul>
        <p>You may disable cookies through your browser settings. However, some features of the website may not function properly if cookies are disabled.</p>
      </>
    ),
  },
  {
    title: '4. Data Security',
    body: (
      <>
        <p>We implement reasonable administrative, technical, and physical safeguards to protect your personal information from unauthorized access, misuse, alteration, disclosure, or destruction.</p>
        <p>While we take reasonable steps to secure your information, no method of transmission over the Internet or electronic storage is completely secure. Therefore, we cannot guarantee absolute security.</p>
      </>
    ),
  },
  {
    title: '5. Sharing of Information',
    body: (
      <>
        <p>We do <strong>not</strong> sell, rent, or trade your personal information.</p>
        <p>Your information may be shared only in the following circumstances:</p>
        <h4>Service Providers</h4>
        <p>We may share your information with trusted third-party service providers, including:</p>
        <ul>
          <li>Payment Gateway Providers</li>
          <li>SMS and Email Service Providers</li>
          <li>Technical Support Partners</li>
          <li>Installation and Maintenance Teams</li>
        </ul>
        <p>These providers are authorized to use your information only as necessary to provide services on our behalf.</p>
        <h4>Legal Requirements</h4>
        <p>We may disclose your information when required by law or when necessary to:</p>
        <ul>
          <li>Comply with legal obligations</li>
          <li>Protect our rights, property, or services</li>
          <li>Prevent fraud or unlawful activities</li>
          <li>Respond to lawful requests from government or regulatory authorities</li>
        </ul>
      </>
    ),
  },
  {
    title: '6. Your Rights',
    body: (
      <>
        <p>Subject to applicable laws, you have the right to:</p>
        <ul>
          <li>Access your personal information</li>
          <li>Correct or update inaccurate information</li>
          <li>Request deletion of your personal information (where legally permitted)</li>
          <li>Withdraw consent for marketing communications</li>
          <li>Request information about how your personal data is used</li>
        </ul>
        <p>To exercise any of these rights, please contact us using the details below.</p>
      </>
    ),
  },
  {
    title: '7. Third-Party Websites',
    body: (
      <p>Our website may contain links to third-party websites or services. We are not responsible for the privacy practices, content, or security of those websites. We encourage you to review their privacy policies before sharing any personal information.</p>
    ),
  },
  {
    title: "8. Children's Privacy",
    body: (
      <>
        <p>Our services are intended for individuals who are <strong>18 years of age or older</strong>.</p>
        <p>We do not knowingly collect personal information from children under the age of 18. If we become aware that such information has been collected, we will take reasonable steps to delete it promptly.</p>
      </>
    ),
  },
  {
    title: '9. Changes to This Privacy Policy',
    body: (
      <p>We may update this Privacy Policy from time to time to reflect changes in our services, business practices, or legal requirements. Any updates will be posted on this page. Your continued use of our website after any changes are posted constitutes your acceptance of the updated Privacy Policy.</p>
    ),
  },
];

export default function PrivacyPolicyPage() {
  const legal = useLegal();
  const navigate = useNavigate();

  return (
    <div className="privacy-page">
      <style>{`
        .privacy-page { min-height: 100vh; background: #f8fafc; padding-bottom: 48px; }
        .privacy-header {
          background: radial-gradient(circle at 50% 0%, #1e3a8a 0%, #0c1a3d 55%, #060e24 100%);
          padding: 22px 20px calc(28px + env(safe-area-inset-top, 0px));
          padding-top: calc(22px + env(safe-area-inset-top, 0px));
          color: #fff;
        }
        .privacy-header-top { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
        .privacy-back-btn {
          display: flex; align-items: center; justify-content: center; width: 34px; height: 34px;
          border-radius: 10px; border: 1px solid rgba(255,255,255,.3); background: rgba(255,255,255,.08);
          color: #fff; cursor: pointer;
        }
        .privacy-title-row { display: flex; align-items: center; gap: 10px; }
        .privacy-title { font-size: 1.3rem; font-weight: 800; margin: 0; }
        .privacy-subtitle { font-size: .8rem; color: rgba(255,255,255,.65); margin-top: 4px; }
        .privacy-body { max-width: 760px; margin: -14px auto 0; padding: 0 16px; }
        .privacy-card {
          background: #fff; border-radius: 16px; border: 1px solid #e5e7eb;
          box-shadow: 0 8px 24px rgba(0,0,0,.06); padding: 24px 22px; margin-bottom: 18px;
        }
        .privacy-card h3 { font-size: 1.02rem; font-weight: 800; color: #111827; margin: 0 0 10px; }
        .privacy-card h4 { font-size: .88rem; font-weight: 700; color: #1e3a8a; margin: 14px 0 6px; }
        .privacy-card p { font-size: .88rem; line-height: 1.65; color: #374151; margin: 0 0 8px; }
        .privacy-card ul { margin: 0 0 8px; padding-left: 20px; }
        .privacy-card li { font-size: .88rem; line-height: 1.65; color: #374151; margin-bottom: 3px; }
        .privacy-contact-item { display: flex; align-items: center; gap: 8px; font-size: .85rem; color: #374151; margin-bottom: 8px; }
        .privacy-contact-item a { color: var(--primary, #1e3a8a); text-decoration: none; font-weight: 600; }
      `}</style>

      <div className="privacy-header">
        <div className="privacy-header-top">
          <button type="button" className="privacy-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft size={17} />
          </button>
        </div>
        <div className="privacy-title-row">
          <ShieldCheck size={22} />
          <h1 className="privacy-title">Privacy Policy</h1>
        </div>
        <div className="privacy-subtitle">{legal.name} &middot; applies to {legal.portalLabel}</div>
      </div>

      <div className="privacy-body">
        <div className="privacy-card">
          <p style={{ marginBottom: 0 }}>
            At <strong>{legal.name}</strong> ("we," "our," or "us"), we respect your privacy and are committed to
            protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect
            your information when you access or use our customer portal at{' '}
            <a href={legal.portalUrl} target="_blank" rel="noreferrer">{legal.portalUrl}</a>.
            By accessing or using our website or services, you agree to the practices described in this Privacy Policy.
          </p>
        </div>

        {SECTIONS.map((s) => (
          <div className="privacy-card" key={s.title}>
            <h3>{s.title}</h3>
            {s.body}
          </div>
        ))}

        <div className="privacy-card">
          <h3>10. Contact Us</h3>
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal information, please contact us:</p>
          <p style={{ fontWeight: 700, color: '#111827', marginBottom: 10 }}>{legal.name}</p>
          <div className="privacy-contact-item">
            <Globe size={14} />
            <a href={legal.portalUrl} target="_blank" rel="noreferrer">{legal.portalLabel}</a>
          </div>
          {legal.email && (
          <div className="privacy-contact-item">
            <Mail size={14} />
            <a href={`mailto:${legal.email}`}>{legal.email}</a>
          </div>
          )}
          <div className="privacy-contact-item" style={{ marginBottom: 0 }}>
            <Phone size={14} />
            <a href={`tel:${legal.phoneHref}`}>{legal.phone}</a>
          </div>
          <p style={{ marginTop: 10, marginBottom: 0 }}>We will make reasonable efforts to respond to your inquiry as promptly as possible.</p>
        </div>

        <div style={{ textAlign: 'center', fontSize: '.78rem', color: '#94a3b8' }}>
          <Link to="/portal/login" style={{ color: 'var(--primary, #1e3a8a)', fontWeight: 600, textDecoration: 'none' }}>
            &larr; Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
